import { DataGridColumn, applyFormat, DataGridColumnFormatter, applyColumnFormat } from './DataGrid.Column';
import * as Format from './Format';
import { Binding } from 'core/infrastructure';

export type ColumnFilterOperator = '==' | '!=' | '<' | '<=' | '>' | '>=' | 'empty' | 'non_empty' | 'starts_with' | 'contains';
export type ColumnDataType = 'number' | 'date' | 'datetime' | 'boolean' | 'string' | 'other';

export type ColumnInfo = {
	type: ColumnDataType,
	nullable: boolean,
	isEmpty: boolean
};

export type ColumnFilter<T> = {
	propertyPath: string;
	dataType: ColumnDataType;
	operator: ColumnFilterOperator;
	// Argumento para la comparación. Si el tipo no es number o date o datetime, será un string
	// que se usará para comparar con el resultado de aplicar la función de formato
	// al valor que elemento al que se aplica el filtro
	argument?: number | Date | string;
	format?: DataGridColumnFormatter<T>;
};

export function matchesFilter<T>(item: T, filter: ColumnFilter<T>[]): boolean {
	return filter.every(filter => isMatch(item, filter));
}

function isMatch<T>(item: T, filter: ColumnFilter<T>): boolean {
	const itemValue = itemValueToCompare(item, filter);
	const filterArgument = filterArgumentToCompare(filter);

	switch (filter.operator) {
		// eslint-disable-next-line eqeqeq
		case '==': return itemValue == filterArgument;
		// eslint-disable-next-line eqeqeq
		case '!=': return itemValue != filterArgument;
		case '<=': return filterArgument !== undefined && itemValue <= filterArgument;
		case '<': return filterArgument !== undefined && itemValue < filterArgument;
		case '>=': return filterArgument !== undefined && itemValue >= filterArgument;
		case '>': return filterArgument !== undefined && itemValue > filterArgument;
		case 'empty': return itemValue == null || itemValue === '';
		case 'non_empty': return itemValue != null && itemValue !== '';
		case 'starts_with': return typeof itemValue === 'string' &&
			(itemValue as string).toLowerCase().indexOf((filterArgument as string).toLowerCase()) === 0;
		case 'contains': return typeof itemValue === 'string' &&
			(itemValue as string).toLowerCase().indexOf((filterArgument as string).toLowerCase()) !== -1;
	}
}

function itemValueToCompare<T>(item: T, filter: ColumnFilter<T>) {
	const value = Binding.getValue(item, filter.propertyPath);

	// Las fechas se comparan como números y se ignoran todas las horas y segundos
	if (filter.dataType === 'date') {
		const d = new Date(+value);
		d.setHours(0);
		d.setMinutes(0);
		d.setSeconds(0);
		d.setMilliseconds(0);
		return d.getTime();
	}


	// Las fechas con hora se comparan como números ignorando segundos, porque
	// en muchos casos no se pintan en el grid y hace imposible saber el valor exacto
	if (filter.dataType === 'datetime') {
		const d = new Date(+value);
		d.setSeconds(0);
		d.setMilliseconds(0);
		return d.getTime();
	}

	// Número y strings se comparan con su valor real
	if (filter.dataType === 'number' || filter.dataType === 'string')
		return value;

	// El resto de cosas, por el valor formateado
	return applyFormat(filter.format, value, item);
}

function filterArgumentToCompare<T>(filter: ColumnFilter<T>) {
	if (filter.argument === null || filter.argument === undefined)
		return filter.argument;

	// Las fechas se comparan como números
	if (filter.dataType === 'date') {
		// Si es una fecha ignoro las horas que pudiera llevar. Hay campos que son datetimes y se formatean como fechas
		// para esos casos ignoro las horas y los segundos
		const d = new Date(+filter.argument);
		d.setHours(0);
		d.setMinutes(0);
		d.setSeconds(0);
		d.setMilliseconds(0);
		return d.getTime();
	}

	// Las fechas con hora se comparan como números ignorando segundos, porque
	// en muchos casos no se pintan en el grid y hace imposible saber el valor exacto
	if (filter.dataType === 'datetime') {
		const d = new Date(+filter.argument);
		d.setSeconds(0);
		d.setMilliseconds(0);
		return d.getTime();
	}


	return filter.argument;
}

export function createFilters<T>(column: DataGridColumn<T>, items: T[]): ColumnFilter<T>[] {
	const { type, nullable, isEmpty } = getColumnInfo(column, items);

	if (isEmpty)
		return [];

	const newFilter = (operator: ColumnFilterOperator) => ({
		dataType: type,
		propertyPath: Binding.propertyPathToString(column.linkTo),
		format: column.format,
		operator
	});

	const filters: ColumnFilter<T>[] = [];

	if (type === 'boolean') {
		filters.push(
			newFilter('=='),
			newFilter('!='));
	}
	else {
		filters.push(
			newFilter('=='), newFilter('!='),
			newFilter('<'), newFilter('<='),
			newFilter('>'), newFilter('>='));
	}

	if (['string', 'other'].includes(type)) {
		filters.push(
			newFilter('starts_with'),
			newFilter('contains'));
	}

	if (nullable) {
		filters.push(
			newFilter('empty'),
			newFilter('non_empty')
		);
	}

	return filters;
}

function inferType(value: any): ColumnDataType {
	const type = typeof value;
	if (type === 'number')
		return 'number';
	if (type === 'boolean')
		return 'boolean';
	if (type === 'string')
		return 'string';
	if (value instanceof Date)
		return 'date';
	return 'other';
}

export function getColumnInfo<T>(column: DataGridColumn<T>, items: T[]): ColumnInfo {

	const path = Binding.propertyPathToString(column.linkTo);

	let type: Nullable<ColumnDataType> = null;
	let nullable = false;
	let isEmpty = true;

	for (let i = 0; i < items.length; i++) {
		// Si ya tengo todos los datos claros, evito seguir recorriendo el grid
		if (type && nullable && !isEmpty)
			break;

		const value = Binding.getValue(items[i], path, true);
		isEmpty = isEmpty && (value == null || value === '');
		nullable = nullable || value == null || value === '';
		type = type || inferType(value);
	}

	if (type === 'number' && looksLikeAnEnum(column, items)) {
		type = 'other';
	}

	if (type === 'date' && looksLikeADateTime(column, items)) {
		type = 'datetime';
	}

	return { type: type || 'other', nullable, isEmpty };
}

function looksLikeADateTime<T>(column: DataGridColumn<T>, _items: T[]): boolean {
	// De momento me baso sólo en el formato, pero podría intentar hacerlo más
	// listo y asumir que es 'datetime' si hay items con distintos valores
	// para la parte hora
	return column.format === Format.asDateAndTime;
}

function looksLikeAnEnum<T>(column: DataGridColumn<T>, items: T[]): boolean {
	// Si no estamos aplicando formato, seguramente no sea un enum
	if (!column.format)
		return false;

	const path = Binding.propertyPathToString(column.linkTo);

	// Si hay algún item que al formatearlo genera letras, seguramente no sea un
	// number y sea un enum. En cualquier caso lo trato como tal
	return items.some(item => {
		const value = Binding.getValue(item, path, true);
		const formatted = applyColumnFormat(column, value, item);
		return !!formatted.match(/[a-z]/i);
	});
}

export function requiresArgument(operator: ColumnFilterOperator): boolean {
	return operator !== 'empty' && operator !== 'non_empty';
}

export function getPossibleArguments<T>(filter: ColumnFilter<T>, items: T[]): string[] {
	if (filter.dataType === 'date' || filter.dataType === 'number')
		return [];

	const MAX_ARGS = 1000;

	// No uso un map ni nada bonito para poder salir del bucle cuando antes si
	// hay demasiado valores distintos

	const args: { [key: string]: string } = {};
	for (let i = 0; i < items.length; i++) {
		const value = itemValueToCompare(items[i], filter) as string;
		if (value) {
			args[value] = value;
			if (Object.keys(args).length > MAX_ARGS)
				return [];
		}
	}

	const keys = Object.keys(args);
	keys.sort((a, b) => a.localeCompare(b));

	return keys;
}

